/* Resize / re-encode an uploaded image to a standard size before sending.
   - Max long edge: 1280px
   - Output: JPEG @ q=0.85
   - Skips re-encoding if the input is already a small JPEG/PNG.
   Returns a Promise<File>.
*/
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.85;
const SKIP_BYTES = 200 * 1024; // 200KB → skip if already small

export async function standardizeImage(input) {
  if (!input) return null;
  const file = input instanceof File ? input : new File([input], "pasted.png", { type: input.type || "image/png" });

  // Fast-path: if already small, return as-is.
  if (file.size <= SKIP_BYTES && /jpeg|png|webp/i.test(file.type)) {
    return file;
  }

  const bitmap = await loadBitmap(file);
  const { width, height } = bitmap;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
  );
  if (!blob) return file;
  return new File([blob], (file.name || "image").replace(/\.[a-z]+$/i, "") + ".jpg", {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file); } catch (_) { /* fall through */ }
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}
